// Security utilities for the Crisci Create platform

// Rate limiting implementation for survey submissions
class RateLimiter {
  private attempts: Map<string, { count: number; firstAttempt: number }> = new Map();
  private readonly maxAttempts: number;
  private readonly windowMs: number;

  constructor(maxAttempts: number = 3, windowMs: number = 15 * 60 * 1000) { // 15 minutes
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
  }

  isAllowed(identifier: string): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(identifier);

    if (!attempts) {
      this.attempts.set(identifier, { count: 1, firstAttempt: now });
      return true;
    }

    // Reset if window has passed
    if (now - attempts.firstAttempt > this.windowMs) {
      this.attempts.set(identifier, { count: 1, firstAttempt: now });
      return true;
    }

    // Check if under limit
    if (attempts.count < this.maxAttempts) {
      attempts.count++;
      return true;
    }

    return false;
  }

  getRemainingTime(identifier: string): number {
    const attempts = this.attempts.get(identifier);
    if (!attempts) return 0;

    const elapsed = Date.now() - attempts.firstAttempt;
    return Math.max(0, this.windowMs - elapsed);
  }
}

// Create rate limiter instances
export const surveySubmissionLimiter = new RateLimiter(2, 60 * 60 * 1000); // 2 submissions per hour
export const loginAttemptLimiter = new RateLimiter(5, 15 * 60 * 1000); // 5 attempts per 15 minutes

// Input sanitization functions
export const sanitizeInput = (input: string): string => {
  if (typeof input !== 'string') return '';

  // Remove potential XSS characters and trim
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<[^>]*>/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+=/gi, '')
    .trim();
};

// Validate email format
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate age input
export const isValidAge = (age: number): boolean => {
  return Number.isInteger(age) && age >= 18 && age <= 120;
};

// Validate survey completion time (prevent bot submissions)
export const isValidSubmissionTime = (startTime: number, minTime: number = 60000): boolean => {
  const completionTime = Date.now() - startTime;
  return completionTime >= minTime; // Minimum 1 minute to complete
};

// Generate session identifier for rate limiting
export const generateSessionId = (): string => {
  // Use a combination of factors for session identification
  const userAgent = navigator.userAgent || '';
  const language = navigator.language || '';
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const screenSize = `${window.screen.width}x${window.screen.height}`;

  // Create a simple hash
  const identifier = `${userAgent}-${language}-${timezone}-${screenSize}`;
  return btoa(identifier).replace(/[^a-zA-Z0-9]/g, '').substring(0, 32);
};

// Cookie consent management
export class CookieConsent {
  private static readonly CONSENT_KEY = 'crisci_curate_consent';

  static hasConsent(): boolean {
    try {
      const consent = localStorage.getItem(this.CONSENT_KEY);
      return consent === 'true';
    } catch {
      return false;
    }
  }

  static setConsent(consent: boolean): void {
    try {
      localStorage.setItem(this.CONSENT_KEY, consent.toString());
    } catch {
      // Ignore if localStorage is not available
    }
  }

  static getConsentDate(): Date | null {
    try {
      const date = localStorage.getItem(`${this.CONSENT_KEY}_date`);
      return date ? new Date(date) : null;
    } catch {
      return null;
    }
  }

  static recordConsent(): void {
    try {
      this.setConsent(true);
      localStorage.setItem(`${this.CONSENT_KEY}_date`, new Date().toISOString());
    } catch {
      // Ignore if localStorage is not available
    }
  }
}

// Secure data validation for survey responses
export const validateSurveyData = (data: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Check required fields
  if (!data.patientInitials || typeof data.patientInitials !== 'string') {
    errors.push('Patient initials are required');
  } else if (data.patientInitials.length > 3) {
    errors.push('Patient initials must be 3 characters or less');
  }

  if (!data.age || !isValidAge(data.age)) {
    errors.push('Valid age (18-120) is required');
  }

  if (!data.gender || !['Male', 'Female', 'Non-binary', 'Prefer not to say'].includes(data.gender)) {
    errors.push('Valid gender selection is required');
  }

  if (!data.productName || typeof data.productName !== 'string' || data.productName.trim().length < 2) {
    errors.push('Product name is required (minimum 2 characters)');
  }

  if (!data.clinicalConditions || !Array.isArray(data.clinicalConditions) || data.clinicalConditions.length === 0) {
    errors.push('At least one clinical condition must be selected');
  }

  if (!data.weeksOfUse || !Number.isInteger(data.weeksOfUse) || data.weeksOfUse < 3) {
    errors.push('Duration of use must be at least 3 weeks');
  }

  if (typeof data.previousCannabisUse !== 'boolean') {
    errors.push('Previous cannabis use indication is required');
  }

  if (!data.satisfactionRating || !Number.isInteger(data.satisfactionRating) ||
      data.satisfactionRating < 1 || data.satisfactionRating > 5) {
    errors.push('Satisfaction rating (1-5) is required');
  }

  // Sanitize text fields
  if (data.sideEffects) {
    data.sideEffects = sanitizeInput(data.sideEffects);
  }
  if (data.additionalFeedback) {
    data.additionalFeedback = sanitizeInput(data.additionalFeedback);
  }
  if (data.previousProducts) {
    data.previousProducts = sanitizeInput(data.previousProducts);
  }
  if (data.otherCondition) {
    data.otherCondition = sanitizeInput(data.otherCondition);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Content Security Policy headers (for deployment)
export const getCSPHeader = (): string => {
  return [
    "default-src 'self'",
    "script-src 'self' 'unsafe-inline' https://apis.google.com https://www.googleapis.com",
    "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
    "font-src 'self' https://fonts.gstatic.com",
    "img-src 'self' data: https:",
    "connect-src 'self' https://firestore.googleapis.com https://identitytoolkit.googleapis.com https://*.cloudfunctions.net https://*.run.app",
    "frame-src 'none'",
    "object-src 'none'",
    "media-src 'none'",
    "form-action 'self'",
    "base-uri 'self'"
  ].join('; ');
};

// Audit logging for security events
export class SecurityAudit {
  private static logs: Array<{
    timestamp: Date;
    event: string;
    details?: any;
    severity: 'info' | 'warning' | 'error';
  }> = [];

  static async log(event: string, details?: any, severity: 'info' | 'warning' | 'error' = 'info'): Promise<void> {
    const logEntry = {
      timestamp: new Date(),
      event,
      details,
      severity
    };

    // Store locally for offline fallback
    this.logs.push(logEntry);

    // Keep only last 100 logs in memory
    if (this.logs.length > 100) {
      this.logs = this.logs.slice(-100);
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Security Audit] ${severity.toUpperCase()}: ${event}`, details);
    }

    // Persist to Firestore via Cloud Function
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('./firebase');
      const createAuditLogFn = httpsCallable(functions, 'createAuditLog');

      await createAuditLogFn({ event, severity, details });
    } catch (error) {
      console.error('Failed to persist audit log:', error);
      // Don't throw - audit logging failure shouldn't break user experience
    }
  }

  static getLogs(): typeof SecurityAudit.logs {
    return [...this.logs];
  }

  static getLogsByEvent(event: string): typeof SecurityAudit.logs {
    return this.logs.filter(log => log.event === event);
  }
}

// Environment variable validation
export const validateEnvironment = (): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Check Firebase configuration
  if (!process.env.REACT_APP_FIREBASE_API_KEY) {
    errors.push('Firebase API key is not configured');
  }

  if (!process.env.REACT_APP_FIREBASE_AUTH_DOMAIN) {
    errors.push('Firebase auth domain is not configured');
  }

  if (!process.env.REACT_APP_FIREBASE_PROJECT_ID) {
    errors.push('Firebase project ID is not configured');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// GDPR compliance helpers
export class GDPRCompliance {
  static readonly DATA_RETENTION_DAYS = 2555; // 7 years for medical data

  static isDataExpired(submissionDate: Date): boolean {
    const now = new Date();
    const retentionEndDate = new Date(submissionDate);
    retentionEndDate.setDate(retentionEndDate.getDate() + this.DATA_RETENTION_DAYS);

    return now > retentionEndDate;
  }

  static generateDataSubjectRequest(): string {
    return `Data subject access request initiated at ${new Date().toISOString()}`;
  }

  static getRetentionPolicy(): string {
    return `Survey data is retained for ${this.DATA_RETENTION_DAYS} days (${Math.floor(this.DATA_RETENTION_DAYS / 365)} years) in compliance with medical data retention requirements.`;
  }
}

// Initialize security measures
export const initializeSecurity = (): void => {
  // Log initialization
  SecurityAudit.log('Security initialization started', {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent
  });

  // Validate environment
  const envValidation = validateEnvironment();
  if (!envValidation.isValid) {
    SecurityAudit.log('Environment validation failed', envValidation.errors, 'error');
  }

  // Set up CSP (this would be done at the server level in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('CSP Header:', getCSPHeader());
  }

  SecurityAudit.log('Security initialization completed');
};